import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <SEO
        title="Handelsbetingelser | MinFesttale.dk"
        description="Læs vores handelsbetingelser, fortrydelsesret og leveringsoplysninger. MinFesttale.dk drives af LearnIQ ApS, CVR 42111007."
        path="/handelsbetingelser"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Tilbage til forsiden
          </Button>
        </Link>

        <Card className="border-border/40">
          <CardContent className="p-6 md:p-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
              Handelsbetingelser
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
              Sidst opdateret: Februar 2026
            </p>

            <div className="prose prose-slate max-w-none space-y-6">
              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  1. Virksomhedsoplysninger
                </h2>
                <p className="text-muted-foreground">
                  LearnIQ ApS<br />
                  CVR: 42111007<br />
                  Adresse: Amaliegade 6, 2. sal, 1256 København K<br />
                  E-mail: hej@minfesttale.dk<br />
                  <span className="text-sm italic">Vi tilbyder kun kundeservice via e-mail (ingen telefonisk support). Vi svarer typisk inden for 24 timer.</span>
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  2. Produkter og priser
                </h2>
                <p className="text-muted-foreground mb-3">
                  MinFesttale.dk tilbyder AI-genererede personlige festtaler.
                </p>
                <p className="text-muted-foreground font-semibold mb-2">Festtaler:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li>Kort tale (~2 min, ca. 400 ord): 149 kr</li>
                  <li>Medium tale (~4 min, ca. 800 ord): 249 kr</li>
                  <li>Lang tale (~6 min, ca. 1200 ord): 299 kr</li>
                </ul>
                <p className="text-muted-foreground">Alle priser er inkl. moms.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  3. Bestilling og betaling
                </h2>
                <p className="text-muted-foreground mb-3">
                  Betaling sker via Stripe med kreditkort, debitkort eller MobilePay. Betalingen gennemføres når du trykker "Køb" efter at have set forhåndsvisningen af din tale.
                </p>
                <p className="text-muted-foreground">
                  Du modtager en kvittering på e-mail umiddelbart efter køb.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  4. Levering
                </h2>
                <p className="text-muted-foreground mb-3">
                  Dit produkt leveres digitalt med det samme efter betaling:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1 mb-3">
                  <li>Den fulde tekst vises på skærmen</li>
                  <li>Du kan redigere teksten direkte i vores editor</li>
                  <li>Du kan downloade den færdige tale som PDF og ren tekst</li>
                </ul>
                <p className="text-muted-foreground">Der er ingen fysisk levering.</p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  5. Tilfredshedsgaranti
                </h2>
                <p className="text-muted-foreground mb-3">
                  Vi tilbyder 100% tilfredshedsgaranti: Er du ikke tilfreds med din tale, kan du generere en ny version gratis inden for 24 timer efter køb.
                </p>
                <p className="text-muted-foreground">
                  Kontakt os på hej@minfesttale.dk hvis du har brug for yderligere hjælp.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  6. Fortrydelsesret
                </h2>
                <p className="text-muted-foreground mb-3">
                  Da der er tale om digitalt indhold som leveres med det samme, gælder forbrugerens fortrydelsesret ikke, jf. forbrugeraftalelovens § 18, stk. 2, nr. 13.
                </p>
                <p className="text-muted-foreground mb-3">
                  Ved at gennemføre købet accepterer du, at leveringen af det digitale indhold påbegyndes straks, og at du dermed giver afkald på din fortrydelsesret.
                </p>
                <p className="text-muted-foreground">
                  Du informeres om dette inden betaling gennemføres.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  7. Reklamation
                </h2>
                <p className="text-muted-foreground mb-3">
                  Hvis der er fejl i det leverede produkt (f.eks. teknisk fejl der forhindrer download), kontakt os hurtigst muligt på hej@minfesttale.dk.
                </p>
                <p className="text-muted-foreground">
                  Vi bestræber os på at løse eventuelle problemer inden for 24 timer.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  8. Ansvar
                </h2>
                <p className="text-muted-foreground mb-3">
                  MinFesttale.dk er et AI-baseret værktøj der genererer udkast til taler baseret på de oplysninger du giver os. Den genererede tekst er et udgangspunkt som du selv kan tilpasse.
                </p>
                <p className="text-muted-foreground">
                  Vi garanterer ikke at teksten er fejlfri eller passer perfekt til din situation uden tilpasning. Vi anbefaler altid at læse teksten igennem og tilpasse den til dine behov.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  9. Ophavsret
                </h2>
                <p className="text-muted-foreground">
                  Den tale du køber, er din at bruge frit til personlige formål (holde talen, dele med gæster). Du må ikke videresælge eller distribuere teksten kommercielt.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl font-bold text-primary mt-6 mb-3">
                  10. Ændringer
                </h2>
                <p className="text-muted-foreground">
                  Vi forbeholder os retten til at ændre disse betingelser. Ændringer træder i kraft ved offentliggørelse på denne side.
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;
